import { Directive, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

export interface AnimationCompleteEvent {
  target: HTMLElement;
  propertyName: string;
}

/**
 * Directive that listens for CSS transition completion events and emits when animations finish.
 *
 * @example
 * ```html
 * <div appAnimationComplete=".slide-container"
 *      [transitionProperties]="['transform', 'translate3d']"
 *      (animationComplete)="onSlideComplete($event)">
 *   <div class="slide-container">...</div>
 * </div>
 * ```
 *
 * @example
 * // Tab panel transitions
 * ```html
 * <div class="tab-panel"
 *      appAnimationComplete
 *      [transitionProperties]="['opacity']"
 *      (animationComplete)="onTabChange($event)">
 * </div>
 * ```
 */
@Directive({
  selector: '[appAnimationComplete]',
  standalone: true,
})
export class AnimationCompleteDirective implements OnDestroy {
  /**
   * CSS selector for the child element to listen to for transitions.
   * If empty or not provided, listens to the host element directly.
   *
   * @example
   * <div appAnimationComplete></div>
   * <div appAnimationComplete=".content"></div>
   */
  @Input() appAnimationComplete = '';

  // e.g. all slide animations: ['transform', 'translate3d', 'left', 'right']
  // or all transform animations: ['transform', 'translate3d', 'translateX', 'translateY', 'scale', 'rotate']
  @Input() transitionProperties: string[] = ['transform', 'left'];

  @Output() animationComplete = new EventEmitter<AnimationCompleteEvent>();

  private transitionEndHandler?: (event: Event) => void;
  private timeoutId?: number;

  constructor(private elementRef: ElementRef<HTMLElement>) {
    this.setupTransitionListener();
  }

  private setupTransitionListener(): void {
    const element = this.elementRef.nativeElement;
    let targetElement: HTMLElement;

    if (this.appAnimationComplete) {
      targetElement = element.querySelector(this.appAnimationComplete) as HTMLElement;
      if (!targetElement) {
        console.warn(`AnimationCompleteDirective: Element with selector "${this.appAnimationComplete}" not found`);
        return;
      }
    } else {
      targetElement = element;
    }

    this.transitionEndHandler = (event: Event) => {
      const transitionEvent = event as TransitionEvent;

      const isSameElement = transitionEvent.currentTarget === targetElement;
      const isRelevantProperty = this.transitionProperties.includes(transitionEvent.propertyName);
      if (isSameElement && isRelevantProperty) {
        // Stop propagation to prevent multiple handlers from firing
        event.stopPropagation();

        this.clearTimeout();
        this.animationComplete.emit({
          target: targetElement,
          propertyName: transitionEvent.propertyName,
        });
      }
    };

    targetElement.addEventListener('transitionend', this.transitionEndHandler, { capture: true });
  }

  /**
   * Manually trigger animation detection with a fallback timeout.
   * Useful when you need to ensure the animationComplete event fires even if
   * the transitionend event doesn't trigger for some reason.
   */
  public triggerAnimationDetection(timeoutMs = 1500): void {
    this.clearTimeout();

    this.timeoutId = window.setTimeout(() => {
      let targetElement: HTMLElement;
      if (this.appAnimationComplete) {
        targetElement = this.elementRef.nativeElement.querySelector(this.appAnimationComplete) as HTMLElement;
      } else {
        targetElement = this.elementRef.nativeElement;
      }

      if (targetElement) {
        this.animationComplete.emit({
          target: targetElement,
          propertyName: 'timeout',
        });
      }
    }, timeoutMs);
  }

  private clearTimeout(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  ngOnDestroy(): void {
    this.clearTimeout();

    if (this.transitionEndHandler) {
      let targetElement: HTMLElement | null;
      if (this.appAnimationComplete) {
        targetElement = this.elementRef.nativeElement.querySelector(this.appAnimationComplete);
      } else {
        targetElement = this.elementRef.nativeElement;
      }

      if (targetElement) {
        targetElement.removeEventListener('transitionend', this.transitionEndHandler, { capture: false });
      }
    }
  }
}
